import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Grid, Box, IconButton } from '@mui/material';
import { PrivateUserInfoRequestModel, UserInfosModel } from '../../../../models/user/UserInfosModel';
import { useTranslationContext } from '../../../../hooks/translations/translationsProvider';
import TextFieldCustom from '../../../TextField/TextFieldCustom';
import DatePickerCustom from '../../../DatePicker/DatePickerCustom';
import dayjs, { Dayjs } from 'dayjs';
import lastDate18YearsAgo from '../../../../utilities/lastDate18YearsAgo';
import { BLUE_DARK, GRAY_LIGHT, GRAY_MAIN, WHITE, GREEN_LOGO, GREEN_MAIN } from '../../../../theme/colors';
import { getAssociationStatusLabel, getCompanyStatusLabel } from '../../../../helpers/approverFromAssociationHelper';
import { putMeAssociationOrCompany, putMePrivate } from '../../../../hooks/actions/users';
import { useWaiter } from '../../../../hooks/waiter/WaiterProvider';
import { Snackbar, Alert } from '@mui/material';
import SearchNations from '../../../UserInfo/searchNation';
import AlertSaveModal from '../../../UserInfo/AlertSaveModal';
import { Close as CloseIcon } from '@mui/icons-material';
import AdminApproval from './AdminApproval';
import EnableUser from './EnableUser';
import NationalEntityCheckbox from './NationalEntityCheckbox';
import { UpdateByAdminCompanyAssociationInformationsAsync } from '../../../../hooks/actions/admin';
import Conventioned from './Conventioned';
import { ESex } from '../../../../hooks/auth/models';
import SexSelect from '../../../UserInfo/DropDownSex';
import { UpdateByAdminCompanyAssociationInformationsRequest } from '../../../../models/requests/UpdateByAdminCompanyAssociationInformationsRequest';
import { UpdateByAdminCompanyAssociationInformationsResponse } from '../../../../models/responses/UpdateByAdminCompanyAssociationInformationsResponse';
import { UpdateByAdminCompanyAssociationInformationsDTO, mapToUpdateByAdminCompanyAssociationInformationsDTO } from '../../../../models/dtos/UpdateByAdminCompanyAssociationInformationsDTO';
import { extractHttpErrorMessage } from '../../../../helpers/extractHttpErrorMessage';
import { useUserContext } from '../../../../hooks/auth/UserProvider';
import { isDevSuperAdminUser } from '../../../../helpers/authorizedUser';

interface CompanyOrAssociationUserProps {
  user: UserInfosModel; 
  onBack: () => void;
}

const AdminAssociationOrCompanyUser: React.FC<CompanyOrAssociationUserProps> = ({ user, onBack }) => {
  const { t } = useTranslationContext();
  const { user: adminUser } = useUserContext();
  const canManageNationalEntity = isDevSuperAdminUser(adminUser);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UpdateByAdminCompanyAssociationInformationsDTO>({
    userId: user.userId || '', 
    conventioned: user.conventioned,
    enabledUser: user.enabledUser === true,
    isNationalReference: user.isNationalReference === true,
    companyOrAssociationStatus: user.companyOrAssociationStatus || 0,
    email: user.email || '',
    nome: user.companyAssociationInfo?.nome || '',
    cognome: user.companyAssociationInfo?.cognome || '',
    sex: user.companyAssociationInfo?.sex || ESex.Male,
    born: user.companyAssociationInfo?.born || undefined,
    comuneDiNascita: user.companyAssociationInfo?.comuneDiNascita || '',
    statoDiNascita: user.companyAssociationInfo?.statoDiNascita || '',
    codiceFiscale: user.companyAssociationInfo?.codiceFiscale || '',
    telephone: user.telephone || '',
    nomeAziendaAssociazione: user.companyAssociationInfo?.nomeAziendaAssociazione || '',
    addressSedeLegale: user.companyAssociationInfo?.addressSedeLegale || '',
    citySedeLegale: user.companyAssociationInfo?.citySedeLegale || '',
    capSedeLegale: user.companyAssociationInfo?.capSedeLegale,
    nationSedeLegale: user.companyAssociationInfo?.nationSedeLegale || '',
    provinciaSedeLegale: user.companyAssociationInfo?.provinciaSedeLegale || '',
    partitaIvaVat: user.companyAssociationInfo?.partitaIvaVat,
    codiceFiscaleAziendale: user.companyAssociationInfo?.codiceFiscaleAziendale || '',
    websiteAziendale: user.companyAssociationInfo?.websiteAziendale,
    telefonoAziendale: user.companyAssociationInfo?.telefonoAziendale,
    mailRichiestePubblicheAziendale: user.companyAssociationInfo?.mailRichiestePubblicheAziendale,
    websiteSottoscrizioneAssociazioni: user.companyAssociationInfo?.websiteSottoscrizioneAssociazioni,
    socialLink: user.companyAssociationInfo?.socialLink,
  });

  const [showPopup, setShowPopup] = useState(false);
  const {setWaiterView} = useWaiter();
  const [initialFormData, setInitialFormData] = useState({ ...formData }); // Salva i dati iniziali

  const [modalOpen, setModalOpen] = useState(false);
  const bornDate = initialFormData.born ? dayjs(initialFormData.born) : dayjs("01-01-1900")
  const [selectedDate, setSelectedDate] = useState<Dayjs | undefined>(bornDate); 
  const [bornError, setBornError] = useState(false);
  const [bornErrorMessage, setBornErrorMessage] = useState('');
  const [maxData] = useState<Dayjs | undefined>(dayjs(lastDate18YearsAgo()));

  const [mainError, setMainError] = useState<boolean>(false);
  const [mainErrorMex, setMainErrorMex] = useState<string>("");

  const handleChange = (value: string, key: string) => {
    if(key === 'enabledUser' || key === "conventioned" || key === 'isNationalReference'){
      const boolVal = value === 'false' ? false : true;
      setFormData((prev: UpdateByAdminCompanyAssociationInformationsDTO) => ({
        ...prev,
        [key]: boolVal,
      }));
    }
    else if(key === "capSedeLegale" || key === "cap"){
      const numberVal = value === undefined || value === null || value === "" ? 0 : parseInt(value);
      setFormData((prev: UpdateByAdminCompanyAssociationInformationsDTO) => ({
        ...prev,
        [key]: numberVal,
      }));
    }
    else{
      setFormData((prev: UpdateByAdminCompanyAssociationInformationsDTO) => ({
        ...prev,
        [key]: value,
      }));
    }
  };


  const handleEdit = () => {
    setMainError(false); // cancello eventuali errori
    setMainErrorMex("");

    setIsEditing(true);
  };

  const handleSave = () => {
    setMainError(false); // cancello eventuali errori
    setMainErrorMex("");

    handleCloseModal(); // chiudo il modal

    if(JSON.stringify(formData) === JSON.stringify(initialFormData)){ // verifico che i dati non siano gli stessi
      setIsEditing(false);

      setShowPopup(true); // apertura e chiusura del popup
      setTimeout(() => {
        setShowPopup(false);
      }, 10000);

      return;
    }
    setWaiterView(true); // avvio il waiter
    
    // Converti il DTO in Request per l'API
    const requestData: UpdateByAdminCompanyAssociationInformationsRequest = {
      userId: formData.userId,
      nomeAziendaAssociazione: formData.nomeAziendaAssociazione,
      addressSedeLegale: formData.addressSedeLegale,
      citySedeLegale: formData.citySedeLegale,
      capSedeLegale: formData.capSedeLegale,
      provinciaSedeLegale: formData.provinciaSedeLegale,
      nationSedeLegale: formData.nationSedeLegale,
      mailRichiestePubblicheAziendale: formData.mailRichiestePubblicheAziendale,
      telephone: formData.telephone,
      codiceFiscaleAziendale: formData.codiceFiscaleAziendale,
      partitaIvaVat: formData.partitaIvaVat,
      websiteAziendale: formData.websiteAziendale,
      telefonoAziendale: formData.telefonoAziendale,
      socialLink: formData.socialLink,
      websiteSottoscrizioneAssociazioni: formData.websiteSottoscrizioneAssociazioni,
      nome: formData.nome,
      cognome: formData.cognome,
      sex: formData.sex,
      born: formData.born,
      codiceFiscale: formData.codiceFiscale,
      comuneDiNascita: formData.comuneDiNascita,
      statoDiNascita: formData.statoDiNascita,
      companyOrAssociationStatus: formData.companyOrAssociationStatus,
      enabledUser: formData.enabledUser,
      conventioned: formData.conventioned,
      isNationalReference: canManageNationalEntity
        ? formData.isNationalReference
        : initialFormData.isNationalReference,
    };
    
    UpdateByAdminCompanyAssociationInformationsAsync(requestData).then((response: UpdateByAdminCompanyAssociationInformationsResponse) => {
      const updatedDTO = mapToUpdateByAdminCompanyAssociationInformationsDTO(response);
      setFormData(updatedDTO);
      setInitialFormData(updatedDTO);
      setWaiterView(false);
      setIsEditing(false);

      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
      }, 10000);

    }).catch((error: unknown) => {
      setMainError(true);
      setMainErrorMex(extractHttpErrorMessage(error, t("Errore durante la modifica dei dati")));
      setWaiterView(false);
    });
  };

  const handleCancel = () => {
    setMainError(false); // cancello eventuali errori
    setMainErrorMex("");

    setFormData(initialFormData); // Reset dei dati al valore iniziale
    setIsEditing(false);
  };

  // Conditionally render the component output
  if (!user.userId) {
    return <div>{t("Utente privato non disponibile.")}</div>;
  }

  const handleChangeDate = (date: Dayjs | null) => {
    setBornErrorMessage('')
    if(date !== null && date.isValid()){ // se la data è valida mi setti false, ma se per caso dovesse essere meno di 18 anni fai true all'errore
        setSelectedDate(date)
        setBornError(false)

        if(maxData !== undefined && date > maxData){
            setBornErrorMessage(t("Devi avere più di 18 anni per iscriverti"))
            setBornError(true)
        } 
        // 🔧 Normalizza la data in formato UTC
        const normalized = new Date(Date.UTC(date.year(), date.month(), date.date()));
        setFormData((prev: UpdateByAdminCompanyAssociationInformationsDTO) => ({
          ...prev,
          ['born']: normalized, // ora sarà "1977-02-01T00:00:00.000Z"
        }));
    } 
    if (date !== null && !date.isValid()){
        setBornErrorMessage(t("Data non valida"))
        setBornError(true)
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  return (
    <Box width='100%'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <IconButton
          onClick={onBack}
          sx={{
            backgroundColor: 'white',
            color: GREEN_MAIN,
            '&:hover': {
              backgroundColor: 'rgba(128,128,128, 0.1)',
            },
            borderRadius: '50%',
            padding: 1,
          }}
        >
          <CloseIcon
            sx={{
              fontSize: '40px',
              '&:hover': {
                fontWeight: 'bold',
              },
            }}
          />
        </IconButton>
      </Box>
      <Typography
        variant="h4"
        component="h1"
        sx={{ fontWeight: "bold", color: GREEN_MAIN, mb: 4 }}
      >
        {t("Informazioni Utente")}
      </Typography>
      <form>
        <Box justifyItems='center'>
          <Grid container xs={12} md={8} spacing={3} paddingBottom='50px'>
            <Grid item xs={12}>
              <Typography sx={{ padding:'14px 0px', color: GREEN_MAIN, fontSize:'0.9rem', textAlign: 'left', width:'100%' }}>
                {t("Dati fiscali dell'attività economica")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextFieldCustom
                label={t("Denominazione")}
                regex={ new RegExp("^[\\p{L}\\p{N}\\p{M}\\p{P}\\p{Z}\\p{S} \\-']+$", "u")}
                value={formData.nomeAziendaAssociazione || ''}
                errorText={t("Richieste lettere e spazi")}
                onChange={(value) => handleChange(value, 'nomeAziendaAssociazione')}
                disable={!isEditing}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Indirizzo")}
                regex={ new RegExp("^[\\p{L}\\p{N}\\p{M}\\p{P}\\p{Z}\\p{S} ,\\.\\-\\/]+$", "u")}
                value={formData.addressSedeLegale || ''}
                onChange={(value) => handleChange(value, 'addressSedeLegale')}
                required={true}
                errorText={t("Richieste solo lettere, spazi, numeri, virgola e meno e /")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Città")}
                regex={ new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                value={formData.citySedeLegale || ''}
                onChange={(value) => handleChange(value, 'citySedeLegale')}
                required={true}
                errorText={t("Richieste lettere e spazi")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextFieldCustom
                label={t("CAP")}
                regex={/^[0-9]+$/}
                value={formData.capSedeLegale === 0 || formData.capSedeLegale === undefined ? '' : formData.capSedeLegale.toString() }
                onChange={(value) => handleChange(value, 'capSedeLegale')}
                required={true}
                errorText={t("Richiesti solo Numeri")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={8} sm={4}>
              <TextFieldCustom
                label={t("Provincia")}
                regex={new RegExp("^[\\p{L} \\-']{2,50}$", "u")}
                value={formData.provinciaSedeLegale || '' }
                onChange={(value) => handleChange(value, 'provinciaSedeLegale')}
                required={true}
                errorText={t("Richieste solo Lettere")}
                disable={!isEditing}
                props={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': { color: GREEN_MAIN },
                    '& .MuiInputLabel-root': { top: -4, fontSize: '1rem' },
                    '& .MuiInputLabel-root.Mui-focused': { color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN_LOGO, color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'grey !important' },
                    '& .MuiOutlinedInput-input': { padding: '12px 12px', lineHeight: 1.4, color: "grey", fontSize: '1rem' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <SearchNations 
                label={t("Stato")}
                nazione={formData.nationSedeLegale || ''} 
                setNazione={(value) => handleChange(value, 'nationSedeLegale')} 
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextFieldCustom
                label={t("E-mail")}
                value={formData.email || '' }
                errorText={t("")}
                props={{ sx:{ '& .MuiOutlinedInput-input': { textTransform: 'lowercase' } } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextFieldCustom
                label={t("E-mail (pubblica)")}
                regex={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
                value={formData.mailRichiestePubblicheAziendale || '' }
                onChange={(value) => handleChange(value, 'mailRichiestePubblicheAziendale')}
                errorText={t("Formato mail non corretto")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldCustom
                label={t("Telefono")}
                regex={/^\+?[0-9\s\-()]{7,20}$/}
                value={formData.telephone?.toString() || '' }
                onChange={(value) => handleChange(value, 'telephone')}
                required={false}
                errorText={t("Richiesti solo Numeri")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Codice Fiscale")}
                regex={/^[A-Za-z0-9]*$/}
                value={formData.codiceFiscaleAziendale || '' }
                onChange={(value) => handleChange(value, 'codiceFiscaleAziendale')}
                required={true}
                errorText={t("Richiesti lettere e numeri")}
                disable={!isEditing}
                props={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': { color: GREEN_MAIN },
                    '& .MuiInputLabel-root': { top: -4, fontSize: '1rem' },
                    '& .MuiInputLabel-root.Mui-focused': { color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN_LOGO, color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'grey !important' },
                    '& .MuiOutlinedInput-input': { padding: '12px 12px', lineHeight: 1.4, color: "grey", fontSize: '1rem' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Partita Iva")}
                regex={/^[A-Za-z0-9]*$/}
                value={formData.partitaIvaVat || '' }
                onChange={(value) => handleChange(value, 'partitaIvaVat')}
                required={false}
                errorText={t("Richiesti lettere e numeri")}
                disable={!isEditing}
                props={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': { color: GREEN_MAIN },
                    '& .MuiInputLabel-root': { top: -4, fontSize: '1rem' },
                    '& .MuiInputLabel-root.Mui-focused': { color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN_LOGO, color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'grey !important' },
                    '& .MuiOutlinedInput-input': { padding: '12px 12px', lineHeight: 1.4, color: "grey", fontSize: '1rem' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Sito internet / pagina social")}
                regex={/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/}
                value={formData.websiteAziendale || '' }
                onChange={(value) => handleChange(value, 'websiteAziendale')}
                errorText={t("Formato sito web non corretto")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Telefono (pubblico)")}
                regex={/^\+?(\d{1,3})?[-. ]?\(?\d{1,4}\)?[-. ]?\d{1,4}[-. ]?\d{1,9}$/}
                value={formData.telefonoAziendale || '' }
                onChange={(value) => handleChange(value, 'telefonoAziendale')}
                errorText={t("Formato telefonico non corretto")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12}>
              <TextFieldCustom
                label={t("URL per eventuali iscrizioni")}
                regex={/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/}
                value={formData.websiteSottoscrizioneAssociazioni || '' }
                onChange={(value) => handleChange(value, 'websiteSottoscrizioneAssociazioni')}
                errorText={t("Formato sito web non corretto")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ padding:'50px 0px 14px 0px', color: GREEN_MAIN, fontSize:'0.9rem', textAlign: 'left', width:'100%' }}>
                { t("Dati del legale rappresentante") }
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Nome")}
                regex={new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                value={formData.nome || ''}
                errorText={t("Richieste lettere e spazi")}
                onChange={(value) => handleChange(value, 'nome')}
                disable={!isEditing}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Cognome")}
                regex={new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                value={formData.cognome || ''}
                errorText={t("Richieste lettere e spazi")}
                onChange={(value) => handleChange(value, 'cognome')}
                disable={!isEditing}
                required
              />
            </Grid>
            <Grid item xs={4} sm={2} >
              <SexSelect 
                value={formData.sex || ESex.Male}
                onChange={(value) => handleChange(value, 'sex')}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={8} sm={4} >
              <DatePickerCustom
                label={t("Data di Nascita")}
                selectedDate={selectedDate}
                handleChangeDate={handleChangeDate}
                maxData={maxData}
                error={bornError}
                errorMessage={bornErrorMessage}
                disable={!isEditing}
                stackProps={{
                  sx: {
                    marginTop:0,
                    marginBottom:0,
                    '& .MuiOutlinedInput-notchedOutline': { color: GREEN_MAIN },
                    '& .MuiInputLabel-root': { top: -4, fontSize: '1rem' },
                    '& .MuiInputLabel-root.Mui-focused': { color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN_LOGO, color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'grey !important' },
                    '& .MuiOutlinedInput-input': { padding: '12px 12px', lineHeight: 1.4, color: "grey", fontSize: '1rem' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldCustom
                label={t("Codice Fiscale")}
                regex={/^[A-Za-z0-9]*$/}
                value={formData.codiceFiscale || '' }
                onChange={(value) => handleChange(value, 'codiceFiscale')}
                required={true}
                errorText={t("Richiesti lettere e numeri")}
                disable={!isEditing}
                props={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': { color: GREEN_MAIN },
                    '& .MuiInputLabel-root': { top: -4, fontSize: '1rem' },
                    '& .MuiInputLabel-root.Mui-focused': { color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN_LOGO, color: GREEN_MAIN },
                    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'grey !important' },
                    '& .MuiOutlinedInput-input': { padding: '12px 12px', lineHeight: 1.4, color: "grey", fontSize: '1rem' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextFieldCustom
                label={t("Comune di nascita")}
                regex={ new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                value={formData.comuneDiNascita || ''}
                onChange={(value) => handleChange(value, 'comuneDiNascita')}
                required={true}
                errorText={t("Richieste lettere e spazi")}
                disable={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <SearchNations 
                label={t("Stato di nascita")}
                nazione={formData.statoDiNascita || ''} 
                setNazione={(value) => handleChange(value, 'statoDiNascita')} 
                disable={!isEditing}
              />
            </Grid>
          </Grid>
        </Box>
      </form>
      {/* --- SEZIONE FINALE: le tre voci richieste --- */}
      <Grid container spacing={2} sx={{ marginTop: '10px', justifyContent: 'space-around'}}>
        <Grid item xs={12} sm={6} md={4} >
          {/* Azienda o Associazione approvata dall'amministrazione */}
          {!isEditing ?
            (
              <Typography sx={{ padding:'16px', color: GREEN_MAIN, fontSize:'0.9rem' }}>
                {t(getCompanyStatusLabel(formData.companyOrAssociationStatus)) }
              </Typography>
            ):(
              <AdminApproval 
                approvedFromAssociation={ formData.companyOrAssociationStatus }
                approvedFromAssociationReset={ initialFormData.companyOrAssociationStatus }
                handleChange={handleChange}
              />
            )
          }
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ marginTop: '10px', justifyContent: 'space-around'}}>
          <Grid item xs={12} sm={6} md={4} >
          {/* Utente Abilitato */}
          {!isEditing ? (
              <Typography sx={{ padding:'16px', color: GREEN_MAIN, fontSize:'0.9rem' }}>
                {formData.enabledUser ? t("Utente Abilitato") : t("Utente Disabilitato")}
              </Typography>
            ) : (
              <EnableUser 
                approved={formData.enabledUser}
                resetValue={initialFormData.enabledUser}
                handleChange={handleChange}
              />
            )
          }
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ marginTop: '10px', justifyContent: 'space-around'}}>
        <Grid item xs={12} sm={6} md={4} >
          {/* Utente Convenzionato */}
          {!isEditing ? (
              <Typography sx={{ padding:'16px', color: GREEN_MAIN, fontSize:'0.9rem' }}>
                {formData.conventioned ? t("Utente Convenzionato") : t("Utente non Convenzionato")}
              </Typography>
            ) : (
              <Conventioned 
                approved={formData.conventioned}
                resetValue={initialFormData.conventioned}
                handleChange={handleChange}
              />
            )
          }
        </Grid>
      </Grid>
      {(canManageNationalEntity && (isEditing || formData.isNationalReference === true)) && (
      <Grid container spacing={2} sx={{ marginTop: '10px', justifyContent: 'space-around'}}>
        <Grid item xs={12} sm={6} md={4} >
          {!isEditing ? (
              <Typography sx={{ padding:'16px', color: GREEN_MAIN, fontSize:'0.9rem', fontWeight: 'bold' }}>
                {t("Entità Nazionale")}
              </Typography>
            ) : (
              <NationalEntityCheckbox
                checked={formData.isNationalReference === true}
                handleChange={handleChange}
              />
            )
          }
        </Grid>
      </Grid>
      )}
      {/* --- Bottoni --- */}
      {!isEditing ? (
        <Grid container justifyContent="center" alignItems="center" spacing={3} sx={{ marginTop: '30px' }}>
          <Grid item xs={6} sm={4} md={2}>
            <Button variant="contained" color="info" onClick={handleEdit} sx={{
              margin:"10px 0 0 0",
              height: '35px',
              color:WHITE,
              backgroundColor: GREEN_LOGO,
              "&:hover": { backgroundColor: "darkgreen" },
              border: "1px solid " + GREEN_LOGO,
            }}>
              {t("Modifica")}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid container justifyContent="center" alignItems="center" spacing={1} sx={{ marginTop: '30px' }}>
          <Grid item xs={6} sm={4} md={2}>
            <Button variant="outlined" onClick={handleCancel} sx={{
              margin:"10px 0 0 0",
              height: '35px',
              "&:hover": { borderColor: "darkgreen" },
            }}>
              {t("Annulla")}
            </Button>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Button variant="contained" color="info" onClick={handleOpenModal} sx={{
              margin:"10px 0 0 0",
              height: '35px',
              color:WHITE,
              backgroundColor: GREEN_LOGO,
              "&:hover": { backgroundColor: "darkgreen" },
              border: "1px solid " + GREEN_LOGO,
            }}>
              {t("Salva")}
            </Button>
          </Grid>
        </Grid>
      )}
      {/* --- Errori e modale --- */}
      {mainError && <>
        <Typography
          variant="body2"
          color="error"
          sx={{
            maxHeight: '6.5em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 5,
            lineHeight: '1.3em',
            wordWrap: 'break-word',
            paddingTop:'20px'
          }}
        >
        {mainErrorMex}
      </Typography>
      </>}
      <AlertSaveModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
      <Snackbar open={showPopup} anchorOrigin={{vertical: 'bottom', horizontal: "right" }}>
        <Alert severity="success"
          sx={{ border: '2px solid green', marginBottom:'30px' }}
          onClose={() => setShowPopup(false)}>
          {t("Salvataggio dati avvenuto con successo")}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminAssociationOrCompanyUser;
